import React from 'react';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '../ui/dialog';
import { Send, Mail, Users, Check } from 'lucide-react';

export function SendReportDialog({
  open, onOpenChange, contacts, contactsLoading,
  selectedContactIds, toggleContact, toggleAllContacts,
  emailList, setEmailList, onSend, senderEmail,
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <Send className="w-5 h-5 text-[#002FA7]" />
            Envoyer le rapport
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-5 mt-4">
          <div className="bg-[#002FA7]/5 border border-[#002FA7]/20 rounded p-3 flex items-center gap-3">
            <Mail className="w-4 h-4 text-[#002FA7] flex-shrink-0" />
            <div>
              <p className="text-xs text-neutral-500">Expediteur</p>
              <p className="text-sm font-medium text-neutral-900" data-testid="sender-email">
                {senderEmail || 'Configuré dans Branding emails'}
              </p>
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <Label className="text-sm font-medium">Contacts du client</Label>
              {contacts.length > 0 && (
                <button onClick={toggleAllContacts} className="text-xs text-[#002FA7] hover:underline" data-testid="toggle-all-contacts-btn">
                  {selectedContactIds.size === contacts.length ? 'Tout deselectionner' : 'Tout selectionner'}
                </button>
              )}
            </div>
            {contactsLoading ? (
              <div className="p-4 text-center">
                <div className="w-5 h-5 border-2 border-neutral-300 border-t-[#002FA7] rounded-full animate-spin mx-auto" />
              </div>
            ) : contacts.length === 0 ? (
              <div className="border border-dashed border-neutral-300 rounded p-4 text-center">
                <Users className="w-8 h-8 mx-auto mb-1 text-neutral-300" />
                <p className="text-sm text-neutral-500">Aucun contact enregistre</p>
                <p className="text-xs text-neutral-400 mt-0.5">Ajoutez des contacts dans la page Clients</p>
              </div>
            ) : (
              <div className="border border-neutral-200 rounded divide-y divide-neutral-100" data-testid="send-contacts-list">
                {contacts.map((contact) => {
                  const isSelected = selectedContactIds.has(contact.id);
                  return (
                    <label key={contact.id}
                      className={`flex items-center gap-3 p-3 cursor-pointer transition-colors ${isSelected ? 'bg-[#002FA7]/5' : 'hover:bg-neutral-50'}`}>
                      <div className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
                        isSelected ? 'bg-[#002FA7] border-[#002FA7]' : 'border-neutral-300'
                      }`}>
                        {isSelected && <Check className="w-3 h-3 text-white" />}
                      </div>
                      <input type="checkbox" className="sr-only" checked={isSelected} onChange={() => toggleContact(contact.id)} />
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-neutral-900">{contact.name}</p>
                        <div className="flex items-center gap-2 text-xs text-neutral-500">
                          <span>{contact.email}</span>
                          {contact.function && (
                            <><span className="text-neutral-300">|</span><span>{contact.function}</span></>
                          )}
                        </div>
                      </div>
                    </label>
                  );
                })}
              </div>
            )}
          </div>

          <div>
            <Label htmlFor="extra-emails">Emails supplementaires</Label>
            <p className="text-xs text-neutral-400 mb-1">Separez les emails par une virgule ou un retour a la ligne</p>
            <textarea id="extra-emails" value={emailList} onChange={(e) => setEmailList(e.target.value)}
              placeholder="email@exemple.com"
              className="flex min-h-[60px] w-full rounded-md border border-neutral-200 bg-white px-3 py-2 text-sm placeholder:text-neutral-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#002FA7] focus-visible:ring-offset-1"
              rows={2} data-testid="extra-emails-input" />
          </div>

          {(selectedContactIds.size > 0 || emailList.trim()) && (
            <div className="bg-neutral-50 rounded p-3 text-sm text-neutral-600">
              {selectedContactIds.size > 0 && (
                <p>{selectedContactIds.size} contact{selectedContactIds.size > 1 ? 's' : ''} selectionne{selectedContactIds.size > 1 ? 's' : ''}</p>
              )}
              {emailList.trim() && (
                <p>{emailList.split(/[,;\n]/).filter(e => e.trim()).length} email(s) supplementaire(s)</p>
              )}
            </div>
          )}

          <div className="flex justify-end gap-3 pt-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>Annuler</Button>
            <Button className="bg-[#002FA7] hover:bg-[#002277]" onClick={onSend}
              disabled={selectedContactIds.size === 0 && !emailList.trim()} data-testid="send-report-confirm-btn">
              <Send className="w-4 h-4 mr-2" /> Envoyer
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
